import React from 'react';
import {Button, FlexRow, SplitButton, Tooltip} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';
import {useBuilderShareDialog} from './useBuilderShareDialog';

/** Top bar shown in view mode. Editors additionally see "Share" and "Edit Report" (opens the edit
 * session via `beginEdit()`, which switches `mode` to `'edit'` once the draft is opened); readers
 * only ever see "Download". Download is a disabled stub (with a tooltip explaining why) — real
 * behaviour lands in a later ticket. Share opens the report-sharing dialog once the report is saved;
 * on an unsaved draft it stays disabled with a tooltip explaining why, since a draft has no
 * Reports-list entry to share. Left-to-right order is Download, Share, Edit Report, matching the
 * design — the reverse of DOM/visual emphasis order used previously. Download uses the same
 * `SplitButton`-with-caret pattern as EditTopBar — every screenshot shows the dropdown caret in both
 * modes. */
export function ViewTopBar(): JSX.Element {
	const {formatMessage} = useIntl();
	const {role, beginEdit, savedReportId, updateSavedReportPrivateAccess} = useReportBuilder();
	const {open: openShare, loading: shareLoading} = useBuilderShareDialog(updateSavedReportPrivateAccess);

	const stubTooltip = formatMessage({id: 'ai_report_builder.stub_action_tooltip'});
	const unsavedShareTooltip = formatMessage({id: 'ai_report_builder.share_requires_save'});

	return (
		<FlexRow justifyContent={'flex-end'} alignItems={'center'} gap={'s'} data-cy={'ai-report-view-top-bar'}>
			<Tooltip content={stubTooltip}>
				<span>
					{/* SplitButton.Section only renders its trailing dropdown caret when
					 * `dropdownMenuOptions` is non-empty (see SplitButton.js), so the caret section
					 * carries a single disabled stub option — the whole control is disabled visually
					 * via the tooltip-wrapped `disabled` prop on both sections; real download-format
					 * behaviour lands in a later ticket. See EditTopBar for the same pattern/rationale. */}
					<SplitButton>
						<SplitButton.Section
							emphasis={'medium'}
							icon={'download'}
							disabled
							text={formatMessage({id: 'ai_report_builder.download'})}
							aria-label={formatMessage({id: 'ai_report_builder.download'})}
						/>
						<SplitButton.Section
							emphasis={'medium'}
							icon={'chevronDown'}
							disabled
							iconOnly
							aria-label={formatMessage({id: 'ai_report_builder.download_options'})}
							dropdownMenuOptions={[
								{
									name: formatMessage({id: 'ai_report_builder.download'}),
									onClick: () => {
										/* disabled option */
									},
									disabled: true,
								},
							]}
						/>
					</SplitButton>
				</span>
			</Tooltip>
			{role === 'editor' ? (
				savedReportId === null ? (
					<Tooltip content={unsavedShareTooltip}>
						<span>
							<Button
								emphasis={'medium'}
								icon={'share'}
								disabled
								data-cy={'ai-report-share-button'}
								aria-label={formatMessage({id: 'ai_report_builder.share'})}
							>
								{formatMessage({id: 'ai_report_builder.share'})}
							</Button>
						</span>
					</Tooltip>
				) : (
					<Button
						emphasis={'medium'}
						icon={'share'}
						disabled={shareLoading}
						onPress={() => openShare(savedReportId)}
						data-cy={'ai-report-share-button'}
						aria-label={formatMessage({id: 'ai_report_builder.share'})}
					>
						{formatMessage({id: 'ai_report_builder.share'})}
					</Button>
				)
			) : null}
			{role === 'editor' ? (
				<Button
					emphasis={'high'}
					icon={'edit'}
					onPress={() => void beginEdit()}
					data-cy={'ai-report-edit-report-button'}
				>
					{formatMessage({id: 'ai_report_builder.edit_report'})}
				</Button>
			) : null}
		</FlexRow>
	);
}
